import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from '@/hooks/useAuth'

export interface EventDetail {
  id: number
  event_time: string
  event_id: number
  message: string
  user_sid?: string
  provider_name?: string
  hostname: string
  record_id?: number | null
  raw?: any
}

export function useEventDetails(
  hostname: string, 
  eventType: 'login_success' | 'login_failure' | 'account_locked' | 'service_crash' | 'app_crash' | 'powershell' | null,
  timeRange: string,
  os?: string
) {
  const { session } = useAuth()
  const [events, setEvents] = useState<EventDetail[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setEvents([])
    setError(null)
    
    if (!session?.access_token || !hostname || !eventType || !timeRange) {
      setLoading(false)
      return
    }

    const fetchEventDetails = async () => {
      setLoading(true)
      setError(null)

      try {
        console.log(`Fetching event details for ${hostname}, type: ${eventType}, range: ${timeRange}, os: ${os}`)

        const { data, error } = await supabase.functions.invoke('event-details', {
          body: { hostname, eventType, timeRange, os: os || 'windows' },
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        })

        if (error) {
          console.error('Error calling event-details function:', error)
          setError(error.message || 'Failed to fetch event details')
          return
        }

        const items = (data?.events || []) as EventDetail[]
        console.log('Received event details count:', items.length)
        setEvents(items)
      } catch (err) {
        console.error('Error in useEventDetails:', err)
        setError('Failed to fetch event details')
      } finally {
        setLoading(false)
      }
    }

    fetchEventDetails()
  }, [session?.access_token, hostname, eventType, timeRange, os])

  return { events, loading, error }
}
